<script lang="ts" setup>
  import type { PropType } from 'vue';
  import type { Breadcrumb, Section } from '$types';

  type Brand = {
    id: number;
    name: string;
    slug: string;
    description_short: string | null;
    photo: string | null;
  }

  defineProps({
    brand: { type: Object as PropType<Brand>, required: true },
    module: Object,
    breadcrumbs: { type: Array as PropType<Breadcrumb[]>, required: true },
    sections: { type: Array as PropType<Section[]>, required: true },
    brands: { type: Array as PropType<Brand[]>, required: true },
    products: { type: Array, required: true },
    productsCount: { type: Number, required: true },
  });
</script>
<template>
  <div class="container">
    <SfBreadcrumbs :breadcrumbs="breadcrumbs" />
    <div class="brands">
      <SfTile class="brand-content">
        <template #title>
          <SfHeading large type="h1">
            {{ brand.name }}
          </SfHeading>
        </template>
        <template #default>
          <p v-if="brand.description_short" v-html="brand.description_short" />
          <SectionRenderer :sections="sections" placement="before" />
          <div v-if="products.length > 0" class="brand-products">
            <ProductCard v-for="product in products" :product horizontal />
            <div class="show-all">
              <SfButton :href="route('web.product.index', { brands: [brand.id] })" primary>
                {{ $t('Zobacz wszystkie produkty') }}
                <span v-if="productsCount > products.length">({{ productsCount }})</span>
              </SfButton>
            </div>
          </div>
          <SfDataPlaceholder v-else class="brands__empty">
            {{ $t('Brak produktów do wyświetlenia') + '.' }}
          </SfDataPlaceholder>
          <SectionRenderer :sections placement="after" />
        </template>
      </SfTile>
      <div class="brands-list">
        <SfHeading type="h2">
          {{ $t('Wszyscy producenci') }}
        </SfHeading>
        <div class="brands-grid">
          <SfTile v-for="brand in brands" :href="route('web.brand.show', { brand: brand })" class="brand" interactive shadow small>
            <MediaPicture :photo="brand.photo" sizes="200x150 x2, 100x75" type="logo" />
            <span class="brand__name">{{ brand.name }}</span>
          </SfTile>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
  @use "$assets/mixins/media";

  .brands {
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 2rem;
    margin-bottom: 120px;

    &__empty {
      margin-top: 2rem;
    }
  }

  .brands-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 0 0 280px;
    font-size: 0.875rem;

    .brands-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .brand {
      box-shadow: var(--sf-brands-list-shadow);

      :deep(.tile__content) {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        padding: 0.25rem 0.5rem;
      }

      :deep(picture) {
        display: contents;

        source {
          display: contents;
        }

        img {
          flex: 0 0 60px;
          width: 60px;
          height: 40px;
        }
      }

      &__name {
        display: flex;
        font-weight: var(--font-weight-bold);
      }

      &.active {
        border: var(--sf-brands-list-active-border);
        box-shadow: var(--sf-brands-list-active-shadow);
        transform: var(--sf-brands-list-active-transform);

        .brand__name {
          color: var(--sf-brands-list-active-color);
        }
      }

      &:hover {
        box-shadow: var(--sf-brands-list-hover-shadow);
      }
    }
  }

  .brand-content {
    width: 100%;

    :deep(.tile__content) {
      justify-content: flex-start;
      align-items: flex-start;
    }
  }

  .brand-products {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
    width: 100%;

    .show-all {
      display: flex;
      justify-content: center;
      margin-top: 2rem;
    }
  }

  @include media.query(lg) {
    .brands {
      flex-direction: column;
      padding: 0 0.5rem;
    }

    .brands-list {
      flex: auto;
      width: 100%;

      .brands-grid {
        grid-template-columns: 1fr 1fr 1fr;
      }

      .brand {
        :deep(.tile__content) {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        &__name {
          text-align: center;
          font-size: 0.625rem;
        }
      }
    }
  }

  @media (max-width: 575px) {
    .brands-list {
      .brands-grid {
        grid-template-columns: 1fr 1fr;
      }
    }
  }
</style>
